import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Check, Ban, X } from 'lucide-react'

interface SelectionBarProps {
  selectedCount: number
  /** Total pending ids visible — drives the select-all checkbox state. */
  visibleCount: number
  allVisibleSelected: boolean
  someVisibleSelected: boolean
  onToggleSelectAll: (checked: boolean) => void
  onApprove: () => void
  onReject: () => void
  onClear: () => void
  busy: boolean
}

type Armed = 'none' | 'approve' | 'reject'

/**
 * Sticky multi-select action bar shown when at least one decision is selected.
 * Approve / reject are two-step (arm → confirm) so a batch is never fired by a
 * single stray click. "Select all visible" lives on the left.
 */
export function SelectionBar({
  selectedCount,
  visibleCount,
  allVisibleSelected,
  someVisibleSelected,
  onToggleSelectAll,
  onApprove,
  onReject,
  onClear,
  busy,
}: SelectionBarProps) {
  const [armed, setArmed] = useState<Armed>('none')

  function fire(action: 'approve' | 'reject') {
    if (armed !== action) {
      setArmed(action)
      return
    }
    if (action === 'approve') onApprove()
    else onReject()
    setArmed('none')
  }

  function handleClear() {
    setArmed('none')
    onClear()
  }

  return (
    <div className="sticky top-0 z-10 flex flex-wrap items-center gap-2 border-b border-primary/40 bg-cmd-container/95 px-3 py-2 backdrop-blur">
      <label className="flex items-center gap-2">
        <Checkbox
          checked={allVisibleSelected}
          indeterminate={someVisibleSelected}
          onCheckedChange={onToggleSelectAll}
          aria-label="Select all visible decisions"
        />
        <span className="font-label text-[0.6rem] tracking-widest text-cmd-outline uppercase">
          Select all visible ({visibleCount})
        </span>
      </label>

      <span className="ml-1 font-mono text-xs tabular-nums text-primary">
        {selectedCount} selected
      </span>

      <div className="ml-auto flex items-center gap-1">
        {armed !== 'none' && (
          <Button
            size="sm"
            variant="ghost"
            disabled={busy}
            onClick={() => setArmed('none')}
            className="font-label text-[0.65rem] tracking-wider"
          >
            CANCEL
          </Button>
        )}
        <Button
          size="sm"
          variant="success"
          disabled={busy || selectedCount === 0}
          onClick={() => fire('approve')}
          className="font-label text-[0.65rem] tracking-wider"
        >
          <Check className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
          {armed === 'approve' ? `CONFIRM · APPROVE ${selectedCount}` : `APPROVE ${selectedCount}`}
        </Button>
        <Button
          size="sm"
          variant="destructive"
          disabled={busy || selectedCount === 0}
          onClick={() => fire('reject')}
          className="font-label text-[0.65rem] tracking-wider"
        >
          <Ban className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
          {armed === 'reject' ? `CONFIRM · REJECT ${selectedCount}` : `REJECT ${selectedCount}`}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          disabled={busy}
          onClick={handleClear}
          className="font-label text-[0.65rem] tracking-wider"
        >
          <X className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
          CLEAR
        </Button>
      </div>
    </div>
  )
}
